import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdOutlineChevronRight, MdOutlineExpandMore } from 'react-icons/md';
import { TEXT_EDITOR_LOCAL_DOCUMENT_ACCEPT } from './textEditorLocalDocumentFile';
import {
  TEXT_EDITOR_MENU_PORTAL_Z_INDEX,
  TEXT_EDITOR_MENU_PORTAL_Z_INDEX_SUBMENU,
} from './textEditorMenuPortal';
import '../Dropdown/Dropdown.css';

const MOBILE_MENU_MQ = '(max-width: 767px)';

function FileMenuRow({
  option,
  onActivate,
  onSubmenuOpen,
  submenuActive,
  useInlineSubmenu,
}) {
  const rowRef = useRef(null);
  const hasSubmenu = Array.isArray(option.submenu) && option.submenu.length > 0;

  const openSubmenu = useCallback(() => {
    if (hasSubmenu && !option.disabled) onSubmenuOpen(rowRef.current, option);
  }, [hasSubmenu, option, onSubmenuOpen]);

  return (
    <div
      ref={rowRef}
      className={[
        'reusable-dropdown-menu-item',
        'wfm-text-editor__file-menu-item',
        option.disabled ? 'reusable-dropdown-menu-item--disabled' : '',
        hasSubmenu && submenuActive ? 'wfm-text-editor__file-menu-item--submenu-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={() => {
        if (!useInlineSubmenu) openSubmenu();
      }}
      onClick={() => {
        if (option.disabled) return;
        if (hasSubmenu) {
          openSubmenu();
          return;
        }
        onActivate(option);
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      aria-disabled={option.disabled ? true : undefined}
      aria-haspopup={hasSubmenu ? 'menu' : undefined}
      aria-expanded={hasSubmenu ? submenuActive : undefined}
    >
      <span className="reusable-dropdown-option-row wfm-text-editor__file-menu-row">
        {option.icon ? (
          <span className="reusable-dropdown-option-icon wfm-text-editor__file-menu-icon" aria-hidden>
            {option.icon}
          </span>
        ) : null}
        <span className="wfm-text-editor__file-menu-label">{option.label}</span>
        {hasSubmenu ? (
          useInlineSubmenu ? (
            <MdOutlineExpandMore
              className={[
                'wfm-text-editor__file-menu-chevron',
                submenuActive ? 'wfm-text-editor__file-menu-chevron--expanded' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden
            />
          ) : (
            <MdOutlineChevronRight className="wfm-text-editor__file-menu-chevron" aria-hidden />
          )
        ) : null}
      </span>
    </div>
  );
}

function FileMenuSubRow({ sub, onActivate }) {
  return (
    <div
      className="reusable-dropdown-menu-item wfm-text-editor__file-menu-item wfm-text-editor__file-menu-item--nested"
      role="menuitem"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={() => onActivate(sub)}
    >
      <span className="reusable-dropdown-option-row wfm-text-editor__file-menu-row">
        {sub.icon ? (
          <span className="reusable-dropdown-option-icon wfm-text-editor__file-menu-icon" aria-hidden>
            {sub.icon}
          </span>
        ) : null}
        <span>{sub.label}</span>
      </span>
    </div>
  );
}

export default function TextEditorFileMenu({ options, onAction, className = '' }) {
  const openInputRef = useRef(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const submenuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [submenuStyle, setSubmenuStyle] = useState(null);
  const [useInlineSubmenu, setUseInlineSubmenu] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MENU_MQ).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MENU_MQ);
    const onChange = () => {
      setUseInlineSubmenu(mq.matches);
      setActiveSubmenu(null);
      setSubmenuStyle(null);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setActiveSubmenu(null);
    setSubmenuStyle(null);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 4;
    const viewportPad = 8;
    const minWidth = 220;
    const maxHeight = useInlineSubmenu ? Math.min(480, window.innerHeight - viewportPad * 2) : 360;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUp = spaceBelow < 180 && rect.top > spaceBelow;
    const maxH = Math.max(120, Math.min(maxHeight, openUp ? rect.top - gap : spaceBelow));
    setMenuPosition({
      position: 'fixed',
      left: Math.max(viewportPad, rect.left),
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      width: 'max-content',
      minWidth,
      maxWidth: useInlineSubmenu ? Math.min(320, window.innerWidth - viewportPad * 2) : 256,
      maxHeight: maxH,
      right: 'auto',
      zIndex: TEXT_EDITOR_MENU_PORTAL_Z_INDEX,
    });
  }, [useInlineSubmenu]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition, options.length, useInlineSubmenu]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocMouseDown = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      const inSub = submenuRef.current?.contains(event.target);
      if (!inRoot && !inMenu && !inSub) closeMenu();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('touchstart', onDocMouseDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('touchstart', onDocMouseDown);
    };
  }, [isOpen, closeMenu]);

  const positionSubmenu = useCallback(
    (rowEl, option) => {
      if (!option?.submenu?.length) return;
      if (useInlineSubmenu) {
        setSubmenuStyle(null);
        setActiveSubmenu((prev) => (prev?.value === option.value ? null : option));
        return;
      }
      if (!rowEl) return;
      const rowRect = rowEl.getBoundingClientRect();
      const viewportPad = 8;
      const subWidth = 232;
      let left = rowRect.right + 2;
      if (left + subWidth > window.innerWidth - viewportPad) {
        left = rowRect.left - subWidth - 2;
      }
      setSubmenuStyle({
        position: 'fixed',
        left: Math.max(viewportPad, left),
        top: rowRect.top,
        width: 'max-content',
        minWidth: subWidth,
        maxWidth: 320,
        right: 'auto',
        zIndex: TEXT_EDITOR_MENU_PORTAL_Z_INDEX_SUBMENU,
      });
      setActiveSubmenu(option);
    },
    [useInlineSubmenu]
  );

  const handleActivate = useCallback(
    (option) => {
      if (!option || option.disabled) return;
      if (option.value === 'open') {
        openInputRef.current?.click();
        closeMenu();
        return;
      }
      onAction(option.value);
      closeMenu();
    },
    [onAction, closeMenu]
  );

  const handleOpenFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    onAction('open-file', { file });
    closeMenu();
  };

  const submenuItems = useMemo(
    () => activeSubmenu?.submenu || [],
    [activeSubmenu]
  );

  const menuEl =
    isOpen && menuPosition ? (
      <div
        ref={menuRef}
        className={[
          'reusable-dropdown-menu',
          'reusable-dropdown-menu--portal',
          'wfm-text-editor__file-dropdown-menu',
          useInlineSubmenu ? 'wfm-text-editor__file-dropdown-menu--inline-sub' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={menuPosition}
        role="menu"
        onMouseDown={(event) => event.stopPropagation()}
      >
        {options.map((option) => (
          <React.Fragment key={option.value}>
            <FileMenuRow
              option={option}
              onActivate={handleActivate}
              onSubmenuOpen={positionSubmenu}
              submenuActive={activeSubmenu?.value === option.value}
              useInlineSubmenu={useInlineSubmenu}
            />
            {useInlineSubmenu &&
            activeSubmenu?.value === option.value &&
            Array.isArray(option.submenu) &&
            option.submenu.length
              ? option.submenu.map((sub) => (
                  <FileMenuSubRow key={sub.value} sub={sub} onActivate={handleActivate} />
                ))
              : null}
          </React.Fragment>
        ))}
      </div>
    ) : null;

  return (
    <>
      <input
        ref={openInputRef}
        type="file"
        accept={TEXT_EDITOR_LOCAL_DOCUMENT_ACCEPT}
        className="wfm-text-editor__file-open-input"
        tabIndex={-1}
        aria-hidden
        onChange={handleOpenFile}
      />
      <div
        ref={rootRef}
        className={['wfm-text-editor__file-dropdown', isOpen ? 'open' : '', className]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          type="button"
          ref={triggerRef}
          className="wfm-text-editor__file-menu-trigger"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span>File</span>
          <MdOutlineExpandMore aria-hidden />
        </button>
        {menuEl ? createPortal(menuEl, document.body) : null}
        {!useInlineSubmenu && activeSubmenu && submenuStyle && submenuItems.length
          ? createPortal(
              <div
                ref={submenuRef}
                className="wfm-text-editor__file-submenu-portal reusable-dropdown-menu"
                style={submenuStyle}
                role="menu"
                onMouseDown={(event) => event.stopPropagation()}
              >
                {submenuItems.map((sub) => (
                  <FileMenuSubRow key={sub.value} sub={sub} onActivate={handleActivate} />
                ))}
              </div>,
              document.body
            )
          : null}
      </div>
    </>
  );
}
